import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@parrot-software/design-system';
import { Info as InfoIcon } from '@parrot-software/design-system/icons';
import { toast } from 'sonner';
import { SubscriptionBlockForm } from '@subscriptions/presentation/components/base';
import type { SubscriptionBlockFormValues } from '@subscriptions/presentation/components/base';
import { AnalyticsStatuses, GroupingActions } from '@subscriptions/presentation/constants';
import { useCreateSubscriptionBlock, useSubscriptionAnalytics } from '@subscriptions/presentation/hooks';
import { resolveApiErrorCode, resolveSubscriptionErrorMessage } from '@subscriptions/presentation/utils';

type BlockCreationStepCreateProps = {
  onCreated: (blockUuid: string) => void;
};

/**
 * Step 1 of the block creation wizard.
 *
 * Renders the title/description/info alert and delegates the field rendering
 * to `SubscriptionBlockForm`. Owns the create mutation, navigates to step 2
 * on success, and surfaces backend error codes as translated toasts.
 *
 * @param props - Component properties
 * @returns React element rendering the step
 */
export const BlockCreationStepCreate = ({ onCreated }: BlockCreationStepCreateProps) => {
  const { t, i18n } = useTranslation('subscriptions');
  const { mutateAsync: createBlock, isLoading } = useCreateSubscriptionBlock();
  const { trackConfigurationFinished, trackConfigurationStarted } = useSubscriptionAnalytics();

  useEffect(() => {
    trackConfigurationStarted({ action: GroupingActions.GROUP, storesCount: 0 });
  }, [trackConfigurationStarted]);

  const handleSubmit = useCallback(
    async (values: SubscriptionBlockFormValues) => {
      try {
        const created = await createBlock({
          name: values.name,
          storeUuids: values.storeUuids,
          groupInvoice: values.groupInvoice,
        });
        trackConfigurationFinished({
          action: GroupingActions.GROUP,
          blockUuid: created.uuid,
          groupInvoices: values.groupInvoice,
          status: AnalyticsStatuses.SUCCESS,
          storesAdded: values.storeUuids.length,
          storesRemoved: 0,
        });
        onCreated(created.uuid);
      } catch (error) {
        trackConfigurationFinished({
          action: GroupingActions.GROUP,
          errorCode: resolveApiErrorCode(error),
          groupInvoices: values.groupInvoice,
          status: AnalyticsStatuses.FAILED,
          storesAdded: values.storeUuids.length,
          storesRemoved: 0,
        });
        toast.error(resolveSubscriptionErrorMessage(error, t, i18n));
      }
    },
    [createBlock, i18n, onCreated, t, trackConfigurationFinished],
  );

  return (
    <div className="my-auto flex w-full flex-col items-center px-6 py-12">
      <div className="flex w-full max-w-[600px] flex-col gap-6">
        <header className="flex flex-col gap-0.5">
          <h1 className="text-2xl font-semibold leading-9 text-foreground">{t('blockCreationWizard.create.title')}</h1>
          <p className="text-base font-normal leading-6 text-foreground">
            {t('blockCreationWizard.create.description')}
          </p>
        </header>

        <Alert className="bg-bg-info border-info text-info">
          <InfoIcon className="text-info! shrink-0" size={16} />
          <AlertDescription className="text-sm">{t('blockCreationWizard.create.alert')}</AlertDescription>
        </Alert>

        <SubscriptionBlockForm isSubmitting={isLoading} onSubmit={handleSubmit} />
      </div>
    </div>
  );
};
